import type { ReactNode } from 'react'
import { InfoTooltip } from '@/components/InfoTooltip'
import { NumericField } from '@/components/NumericField'
import { numberField, parseOptionalNumber } from '@/lib/numberField'
import type { GlobalDefaultsFile } from '@/types/bundle'

export interface LimitsOutputTabProps {
  file: GlobalDefaultsFile
  onChange: (patch: Partial<GlobalDefaultsFile>) => void
}

// The 4 hard caps, each keeping its own id, floor and blank-input default:
// a blank required cap falls back to its floor (max_pdf_size_mb to 0, not 1),
// while the optional max_response_payload_mb is left unset.
const HARD_CAP_FIELDS: {
  field: keyof GlobalDefaultsFile['hard_caps']
  id: string
  label: ReactNode
  tooltip?: string
  min: number
  step?: string
  blankValue: number | undefined
}[] = [
  { field: 'max_documents', id: 'hard-cap-max-documents', label: 'Max documents', min: 1, blankValue: 1 },
  { field: 'max_total_pages', id: 'hard-cap-max-total-pages', label: 'Max total pages', min: 1, blankValue: 1 },
  {
    field: 'max_pdf_size_mb',
    id: 'hard-cap-max-pdf-size',
    label: 'Max PDF size (MB)',
    min: 0,
    step: '0.1',
    blankValue: 0,
  },
  {
    field: 'max_response_payload_mb',
    id: 'hard-cap-max-response-payload',
    label: (
      <>
        Max response payload (MB) <span className="text-text-disabled">(optional)</span>
      </>
    ),
    tooltip: 'Caps the size of the JSON response returned to the caller, including any echoed original documents.',
    min: 0,
    step: '0.1',
    blankValue: undefined,
  },
]

export function LimitsOutputTab({ file, onChange }: LimitsOutputTabProps) {
  const hardCaps = file.hard_caps
  const outputOptions = file.output_options ?? {}
  const ocrFallback = file.ocr_fallback ?? {}

  return (
    <div className="flex flex-col gap-6">
      <section className="flex flex-col gap-4 rounded-control border border-border bg-surface p-4">
        <h2 className="flex items-center gap-1 text-header font-semibold text-text-primary">
          Hard caps
          <InfoTooltip text="Platform-wide request limits, enforced before any extraction begins." />
        </h2>
        <div className="grid grid-cols-2 gap-4">
          {HARD_CAP_FIELDS.map(({ field, id, label, tooltip, min, step, blankValue }) => (
            <NumericField
              key={field}
              id={id}
              label={label}
              tooltip={tooltip}
              min={min}
              step={step}
              value={numberField(hardCaps[field])}
              onChange={(raw) => onChange({ hard_caps: { ...hardCaps, [field]: parseOptionalNumber(raw) ?? blankValue } })}
            />
          ))}
        </div>
      </section>

      <section className="flex flex-col gap-4 rounded-control border border-border bg-surface p-4">
        <h2 className="text-header font-semibold text-text-primary">Output</h2>
        <label className="flex w-fit items-center gap-2 text-body text-text-primary">
          <input
            type="checkbox"
            checked={outputOptions.allow_base64_documents ?? false}
            onChange={(event) => onChange({ output_options: { ...outputOptions, allow_base64_documents: event.target.checked } })}
          />
          Allow echoing original documents
          <InfoTooltip text="The platform-wide gate in the three-gate AND that also requires a product type's and document type's own echo_original_documents flag to be true before any document is actually echoed back." />
        </label>
      </section>

      <section className="flex flex-col gap-4 rounded-control border border-border bg-surface p-4">
        <h2 className="flex items-center gap-1 text-header font-semibold text-text-primary">
          OCR fallback
          <InfoTooltip text="Used when a PDF page has no usable native text layer (e.g. a scanned document) - the page is OCR'd instead." />
        </h2>
        <label className="flex w-fit items-center gap-2 text-body text-text-primary">
          <input
            type="checkbox"
            checked={ocrFallback.enabled ?? false}
            onChange={(event) => onChange({ ocr_fallback: { ...ocrFallback, enabled: event.target.checked } })}
          />
          Enabled
        </label>

        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col gap-1">
            <span className="flex items-center gap-1 text-meta font-medium text-text-secondary">
              Engine
              <InfoTooltip text="Fixed by the platform - tesseract is currently the only supported OCR engine." />
            </span>
            <span className="inline-flex w-fit items-center rounded-pill bg-surface-raised px-2 py-1 text-meta text-text-primary">
              {ocrFallback.engine ?? 'tesseract'}
            </span>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-meta font-medium text-text-secondary" htmlFor="ocr-language">
              Language
            </label>
            <input
              id="ocr-language"
              type="text"
              value={ocrFallback.language ?? ''}
              onChange={(event) => onChange({ ocr_fallback: { ...ocrFallback, language: event.target.value || undefined } })}
              className="rounded-control border border-border bg-bg px-3 py-2 text-body text-text-primary outline-none"
            />
          </div>

          <NumericField
            id="ocr-dpi"
            label="DPI"
            tooltip="Resolution each page is rendered at before being handed to the OCR engine."
            min={1}
            value={numberField(ocrFallback.dpi)}
            onChange={(raw) => onChange({ ocr_fallback: { ...ocrFallback, dpi: parseOptionalNumber(raw) } })}
          />

          <NumericField
            id="ocr-min-native-chars"
            label="Min native chars to skip OCR"
            tooltip="If a page's native text layer already has at least this many characters, OCR is skipped for that page."
            min={0}
            value={numberField(ocrFallback.min_native_chars_to_skip_ocr)}
            onChange={(raw) => onChange({ ocr_fallback: { ...ocrFallback, min_native_chars_to_skip_ocr: parseOptionalNumber(raw) } })}
          />

          <NumericField
            id="ocr-min-accept-chars"
            label="Min OCR chars to accept"
            tooltip="An OCR result shorter than this many characters is treated as a failed read rather than accepted."
            min={0}
            value={numberField(ocrFallback.min_ocr_chars_to_accept)}
            onChange={(raw) => onChange({ ocr_fallback: { ...ocrFallback, min_ocr_chars_to_accept: parseOptionalNumber(raw) } })}
          />
        </div>
      </section>

      <section className="flex flex-col gap-4 rounded-control border border-border bg-surface p-4">
        <h2 className="text-header font-semibold text-text-primary">Debug</h2>
        <label className="flex w-fit items-center gap-2 text-body text-text-primary">
          <input
            type="checkbox"
            checked={file.return_candidates_debug ?? false}
            onChange={(event) => onChange({ return_candidates_debug: event.target.checked })}
          />
          Return candidates (debug)
          <InfoTooltip text="Includes the engine's raw candidate values (before acceptance/normalisation) in the response - a debugging aid, never enable in production." />
        </label>
      </section>
    </div>
  )
}
